/*----------------------------------
- TLD APPLICATIONS: BOARD TRANSPORT
----------------------------------*/

/**
 * @docs docs/features/tld-applications
 * @adr  ADR-0065
 * @rule The board's one request per change: its query, posted to the website gateway, which
 *       normalizes it for the website (A to Z, no member view) before Product sees it. The page's
 *       URL is never written (D28).
 */

import type { PublicTldApplications, TldApplicationsQuery } from '@uniquedomains/components/contracts/tlds/public';

import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import type { PublicReadResult } from '@/common/contracts/publicReadResult';
import { TLD_APPLICATIONS_GATEWAY_PATH, type TldApplicationsGatewayRequest } from '@/common/contracts/tldApplicationsGateway';

/** @boundary The gateway's JSON answer, the shape `server/routes/tldApplications.ts` writes. */
const readGatewayAnswer = async (response: Response): Promise<PublicReadResult<PublicTldApplications>> =>
    (await response.json()) as PublicReadResult<PublicTldApplications>;

export const fetchTldApplications = async (query: TldApplicationsQuery): Promise<PublicTldApplications> => {
    const body: TldApplicationsGatewayRequest = { query };
    const response = await window.fetch(TLD_APPLICATIONS_GATEWAY_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const result = response.ok ? await readGatewayAnswer(response) : null;
    if (!result || result.status !== 'success') throw new Error(TLD_PAGE_CONTENT.applications.unavailable);
    return result.value;
};
